import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeadingBlock } from './blocks/HeadingBlock';
import { ParagraphBlock } from './blocks/ParagraphBlock';
import { ImageBlock } from './blocks/ImageBlock';
import { VideoBlock } from './blocks/VideoBlock';
import { InputBlock } from './blocks/InputBlock';
import { findInputBlock, findStartCard, initialState, reducer, } from './state';
import { interpolate } from './interpolate';
import { evaluateVisibility } from './visibility';
/**
 * Renders a v2 block-based tutorial as a guided, single-card-at-a-time
 * experience. Behavior mirrors the iOS TutorialPlayerViewModel:
 *  - one card visible at a time, advanced by an explicit Continue / Check button
 *  - exactly one input block per card; branches first-match-wins on input value
 *  - terminal cards close the run and surface the final outcome
 *
 * Mode controls variable rendering: `play` substitutes resolved values;
 * `preview` substitutes example values and renders unresolved tokens as chips
 * for the builder pane.
 */
export function TutorialPlayer({ cards, variableValues = {}, variableExamples = {}, outcomes = {}, mode = 'play', onComplete, onCardChange, onError, header, }) {
    const startCard = useMemo(() => findStartCard(cards), [cards]);
    const [state, dispatch] = useReducer(reducer, startCard?.id ?? '', initialState);
    // Track when the player run actually started so we can attribute
    // duration on completion.
    const startedAtRef = useStableRef(() => Date.now());
    const currentCard = useMemo(() => cards.find((c) => c.id === state.currentCardId), [cards, state.currentCardId]);
    const vars = useMemo(() => ({ values: variableValues, examples: variableExamples, mode }), [variableValues, variableExamples, mode]);
    // Surface the start card to consumers (mirrors what onCardChange does for
    // every subsequent transition).
    useEffect(() => {
        if (startCard)
            onCardChange?.(startCard.id);
        // We deliberately fire only on the start card; subsequent card-change
        // notifications are emitted from the dedicated effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startCard?.id]);
    useEffect(() => {
        if (!currentCard || currentCard.id === startCard?.id)
            return;
        onCardChange?.(currentCard.id);
    }, [currentCard?.id, onCardChange, startCard?.id]);
    useEffect(() => {
        if (!state.completedAt || mode === 'preview')
            return;
        const startedAt = startedAtRef.current;
        onComplete?.({
            branchPath: state.branchPath,
            inputValues: state.inputValues,
            terminalOutcome: state.terminalOutcome,
            outcomeCode: state.outcomeCode,
            completedAt: state.completedAt,
            durationSeconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
        });
    }, [state.completedAt, state.branchPath, state.inputValues, state.terminalOutcome, state.outcomeCode, mode, onComplete, startedAtRef]);
    if (!startCard) {
        onError?.(new Error('Tutorial has no cards.'));
        return (_jsx(EmptyState, { message: "This tutorial has no cards yet." }));
    }
    if (!currentCard) {
        onError?.(new Error(`Cannot find card ${state.currentCardId}.`));
        return _jsx(EmptyState, { message: "Something went wrong loading this card." });
    }
    const inputBlock = findInputBlock(currentCard);
    const inputValue = state.inputValues[currentCard.id] ?? '';
    const canAdvance = computeCanAdvance(currentCard, inputBlock, inputValue);
    return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col", children: [header, _jsx("div", { className: header ? 'pt-3' : 'pt-6', children: _jsx(ProgressBar, { state: state, cards: cards }) }), _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto py-6", children: _jsx(AnimatePresence, { mode: "wait", children: _jsx(motion.section, { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.22, ease: 'easeOut' }, className: "flex flex-col gap-5", "aria-live": "polite", children: currentCard.is_terminal ? (_jsx(TerminalCard, { card: currentCard, outcomes: outcomes })) : (_jsxs(_Fragment, { children: [currentCard.blocks
                                    .filter((b) => b.type !== 'input')
                                    .filter((b) => evaluateVisibility(b.visible_when, variableValues))
                                    .map((b) => (_jsx(BlockView, { block: b, vars: vars }, b.id))), inputBlock ? (_jsx(InputBlock, { block: inputBlock, value: inputValue, onChange: (value) => dispatch({ type: 'SUBMIT_INPUT', cardId: currentCard.id, value }), vars: vars })) : null] })) }, currentCard.id) }) }), !state.completedAt ? (_jsx("div", { className: "border-t border-slate-100 pb-[max(env(safe-area-inset-bottom),12px)] pt-4", children: _jsx("button", { type: "button", onClick: () => dispatch({ type: 'ADVANCE', cards }), disabled: !canAdvance || mode === 'preview', className: "flex h-12 w-full items-center justify-center rounded-full bg-teal-500 text-base font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:bg-teal-600", children: currentCard.is_terminal
                        ? 'Done'
                        : inputBlock
                            ? 'Check'
                            : 'Continue' }) })) : null] }));
}
function computeCanAdvance(card, inputBlock, inputValue) {
    if (card.is_terminal)
        return true;
    if (!inputBlock)
        return true;
    if (inputBlock.type !== 'input')
        return true;
    if (!inputBlock.input_required)
        return true;
    return inputValue.trim().length > 0;
}
function BlockView({ block, vars, }) {
    switch (block.type) {
        case 'heading':
            return _jsx(HeadingBlock, { block: block, vars: vars });
        case 'paragraph':
            return _jsx(ParagraphBlock, { block: block, vars: vars });
        case 'image':
            return _jsx(ImageBlock, { block: block });
        case 'video':
            return _jsx(VideoBlock, { block: block });
        case 'input':
            // Inputs render via the dedicated InputBlock at the bottom of the card,
            // not here.
            return null;
        case 'divider':
            return _jsx("hr", { className: "border-slate-200 my-1" });
        case 'spacer': {
            const height = block.size === 'sm' ? 12 : block.size === 'lg' ? 40 : 24;
            return _jsx("div", { style: { height }, "aria-hidden": "true" });
        }
        case 'callout':
            return _jsx(CalloutBlockView, { block: block, vars: vars });
        case 'button':
            return _jsx(ButtonBlockView, { block: block });
        case 'audio':
            return _jsx(AudioBlockView, { block: block });
        case 'embed':
            return _jsx(EmbedBlockView, { block: block });
        case 'accordion':
            return _jsx(AccordionBlockView, { block: block, vars: vars });
        default: {
            // Exhaustiveness guard. Adding a member to the `Block` union without a
            // case above is now a COMPILE error here, not a block that silently
            // vanishes from the player. The warn covers the other direction —
            // content authored against a newer schema than the deployed player.
            const exhaustive = block;
            console.warn('[tutorial-player] unknown block type, skipping:', exhaustive.type);
            return null;
        }
    }
}
function ButtonBlockView({ block, }) {
    // The player currently renders buttons as inert previews; the host app's
    // onComplete/onCardChange wiring handles the action click. The visible
    // shape matches the variant the author chose.
    const variantClass = {
        primary: 'bg-teal-500 text-white border-teal-500 shadow-sm',
        secondary: 'bg-white text-teal-700 border-teal-300',
        subtle: 'bg-transparent text-slate-600 border-transparent underline',
    }[block.variant];
    return (_jsx("button", { type: "button", className: `w-full px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${variantClass}`, children: block.label || 'Continue' }));
}
function AudioBlockView({ block, }) {
    return (_jsxs("div", { className: "rounded-xl border border-slate-200 bg-slate-50 p-3", children: [block.media_url ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            _jsx("audio", { src: block.media_url, controls: true, autoPlay: block.autoplay, className: "w-full" })) : (_jsx("p", { className: "text-xs text-slate-400 italic", children: "No audio yet." })), block.transcript && (_jsxs("details", { className: "mt-2", children: [_jsx("summary", { className: "text-xs font-medium text-slate-600 cursor-pointer hover:text-slate-800", children: "Transcript" }), _jsx("p", { className: "mt-1.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed", children: block.transcript })] }))] }));
}
function EmbedBlockView({ block, }) {
    const embedUrl = resolveEmbedUrl(block.provider, block.url);
    const padding = block.aspect === 'square' ? '100%' : block.aspect === 'tall' ? '125%' : '56.25%';
    return (_jsx("div", { className: "w-full rounded-xl overflow-hidden bg-slate-900 relative", style: { paddingBottom: padding }, children: embedUrl ? (_jsx("iframe", { src: embedUrl, title: "Embedded media", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share", allowFullScreen: true, className: "absolute inset-0 w-full h-full" })) : (_jsxs("div", { className: "absolute inset-0 flex items-center justify-center text-slate-400 text-xs", children: ["Paste a ", block.provider, " URL to preview"] })) }));
}
function AccordionBlockView({ block, vars, }) {
    const html = interpolate(block.content ?? '', vars);
    return (_jsxs("details", { open: block.default_open, className: "rounded-xl border border-slate-200 bg-white overflow-hidden group", children: [_jsxs("summary", { className: "flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 transition-colors list-none", children: [_jsx("span", { children: block.title || 'More info' }), _jsx("span", { className: "text-slate-400 group-open:rotate-180 transition-transform", children: "\u25BE" })] }), _jsx("div", { className: "tutorial-prose px-4 py-3 text-sm leading-relaxed text-slate-700 border-t border-slate-100", 
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML: { __html: html } })] }));
}
/** Best-effort extraction of an embed URL from common share URLs. */
function resolveEmbedUrl(provider, url) {
    if (!url)
        return '';
    try {
        const parsed = new URL(url);
        if (provider === 'youtube') {
            const v = parsed.searchParams.get('v');
            if (v)
                return `https://www.youtube.com/embed/${v}`;
            // youtu.be/<id>
            if (parsed.hostname.includes('youtu.be')) {
                const id = parsed.pathname.replace(/^\//, '');
                return `https://www.youtube.com/embed/${id}`;
            }
            // already an embed URL
            if (parsed.pathname.includes('/embed/'))
                return url;
            return url;
        }
        if (provider === 'vimeo') {
            const m = parsed.pathname.match(/(\d+)/);
            if (m)
                return `https://player.vimeo.com/video/${m[1]}`;
            return url;
        }
        if (provider === 'loom') {
            // https://www.loom.com/share/<id>  →  https://www.loom.com/embed/<id>
            return url.replace('/share/', '/embed/');
        }
        return url;
    }
    catch {
        return '';
    }
}
function CalloutBlockView({ block, vars, }) {
    const palette = {
        info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', accent: 'text-blue-500' },
        warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', accent: 'text-amber-500' },
        success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', accent: 'text-green-600' },
        tip: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-900', accent: 'text-violet-500' },
    }[block.variant];
    const emoji = {
        info: 'ℹ️',
        warning: '⚠️',
        success: '✓',
        tip: '💡',
    }[block.variant];
    const html = interpolate(block.content ?? '', vars);
    return (_jsxs("div", { className: `flex items-start gap-3 rounded-xl border ${palette.bg} ${palette.border} px-4 py-3`, children: [_jsx("span", { className: `text-xl leading-none flex-shrink-0 ${palette.accent}`, "aria-hidden": "true", children: emoji }), _jsxs("div", { className: `min-w-0 flex-1 ${palette.text}`, children: [block.title && _jsx("div", { className: "text-sm font-semibold mb-1", children: block.title }), _jsx("div", { className: "tutorial-prose text-sm leading-relaxed", 
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML: { __html: html } })] })] }));
}
/**
 * Author-configurable end-screen rendering.
 *
 * Consumers pass `outcomes` (a lookup of code → metadata) so the terminal
 * card can show the author's emoji, label, and display_message. Falls
 * back to legacy terminal_outcome semantics when outcomes is empty or
 * the card's code doesn't match anything (e.g. preview of an old card).
 */
function TerminalCard({ card, outcomes, }) {
    const code = card.outcome_code ?? card.terminal_outcome ?? null;
    const outcome = code ? outcomes[code] : undefined;
    const kind = outcome?.kind ?? (card.terminal_outcome === 'failure' ? 'failure' : 'success');
    const emoji = outcome?.emoji ?? (kind === 'failure' ? '😞' : kind === 'neutral' ? '✨' : '🎉');
    const headline = outcome?.label ?? card.title ?? (kind === 'failure' ? 'That was a tough one.' : 'Great work today.');
    const message = outcome?.display_message ??
        (kind === 'failure'
            ? "It's okay — you can come back to this anytime."
            : 'Thanks for taking the time to learn this.');
    // Accent colors: custom hex if set, otherwise kind-default palette.
    const accent = outcome?.color_hex ??
        (kind === 'failure' ? '#e11d48' : kind === 'neutral' ? '#64748b' : '#0d9488');
    return (_jsxs("div", { className: "flex flex-col items-center gap-4 rounded-3xl px-6 py-12 text-center", style: { backgroundColor: accent + '14' }, children: [_jsx("div", { className: "text-6xl leading-none", "aria-hidden": "true", children: emoji }), _jsx("p", { className: "text-2xl font-semibold", style: { color: accent }, children: headline }), _jsx("p", { className: "max-w-sm text-sm text-slate-600", children: message })] }));
}
function ProgressBar({ state, cards }) {
    const total = Math.max(cards.length, 1);
    const visited = Math.min(state.branchPath.length, total);
    const fraction = visited / total;
    return (_jsx("div", { className: "h-1.5 w-full overflow-hidden rounded-full bg-slate-100", role: "progressbar", "aria-valuenow": Math.round(fraction * 100), "aria-valuemin": 0, "aria-valuemax": 100, children: _jsx(motion.div, { className: "h-full rounded-full bg-teal-500", initial: false, animate: { width: `${fraction * 100}%` }, transition: { duration: 0.3, ease: 'easeOut' } }) }));
}
function EmptyState({ message }) {
    return (_jsx("div", { className: "flex flex-col items-center justify-center gap-2 rounded-2xl bg-slate-50 px-6 py-12 text-center text-sm text-slate-500", children: message }));
}
// ── Helpers ────────────────────────────────────────────────────────────────
/** A ref initialized lazily on first render via the provided thunk. */
function useStableRef(init) {
    const ref = useRef(null);
    if (ref.current === null)
        ref.current = init();
    return ref;
}
